import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Fade from "@mui/material/Fade";

const LANGUAGE_KEY = "jcl-ui-language";
const POSITION_KEY = "jcl-ui-language-position";

const EDGE_GAP = 10;

// Keep the canonical React-rendered source for every translated node. This lets
// EN / zh-CN / zh-TW switch in-place without a full browser reload.
const TEXT_SOURCE = new WeakMap();
const ATTRIBUTE_SOURCE = new WeakMap();

function isTranslationExcluded(node) {
  const element = node?.nodeType === Node.TEXT_NODE ? node.parentElement : node;
  if (!element?.closest) return false;

  return Boolean(
    element.closest(
      ".MuiIcon-root, .material-icons, .material-icons-outlined, .notranslate, [translate='no'], [data-jcl-no-translate='true']"
    )
  );
}

function canonicalTextRecord(node) {
  const current = node.nodeValue || "";
  let record = TEXT_SOURCE.get(node);

  // If React replaced the content, treat the new value as the new canonical
  // English source. If the value equals our last rendered translation, keep
  // the original source.
  if (!record || current !== record.rendered) {
    record = { source: current, rendered: current };
    TEXT_SOURCE.set(node, record);
  }

  return record;
}

function canonicalAttributeRecord(node, attribute) {
  const current = node.getAttribute(attribute) || "";
  let records = ATTRIBUTE_SOURCE.get(node);

  if (!records) {
    records = new Map();
    ATTRIBUTE_SOURCE.set(node, records);
  }

  let record = records.get(attribute);
  if (!record || current !== record.rendered) {
    record = { source: current, rendered: current };
    records.set(attribute, record);
  }

  return record;
}

const SAFE_TEXT_REPLACEMENTS = [
  [
    "Application roles are separate from Supabase authentication and are enforced by Row-Level Security.",
    "Manage user access and assigned roles.",
  ],
  [
    "Permissions are enforced by Supabase Row-Level Security, not only hidden in the interface.",
    "Access is based on your assigned role.",
  ],
  [
    "Create authentication users in Supabase, then assign their JCL application role here.",
    "Manage JCL users and access levels.",
  ],
  [
    "No application activity has been recorded yet. Run the activity-log SQL patch once; new notes, follow-ups, claims, campaign-control changes and sent emails will then appear automatically.",
    "No activity records available.",
  ],
  ["No application activity has been recorded yet.", "No activity records available."],
];

const TRANSLATIONS = [
  ["JCL Intelligence", "JCL 智能平台"],
  ["Sales & stock intelligence", "销售与库存智能"],
  ["Sales & Stock Intelligence", "销售与库存智能"],
  ["Sales, stock and campaign intelligence", "销售、库存与推广智能"],
  ["Internal Sales Intelligence", "内部销售智能"],
  ["Authorized internal users only", "仅限授权内部用户"],

  ["Dashboard", "仪表盘"],
  ["SALES WORKSPACE", "销售工作台"],
  ["Sales Workspace", "销售工作台"],
  ["Lead Database", "客户线索库"],
  ["My Leads", "我的线索"],
  ["Follow-ups", "跟进事项"],

  ["INVENTORY INTELLIGENCE", "库存智能"],
  ["Inventory Intelligence", "库存智能"],
  ["Stock Portfolio", "库存概览"],
  ["Promotion Campaigns", "推广活动"],

  ["ANALYSIS & CONTROL", "分析与控制"],
  ["Analysis & Control", "分析与控制"],
  ["GIS", "地图"],
  ["Data Quality", "数据质量"],
  ["Reports", "报告"],
  ["Claim Overview", "认领概览"],
  ["Activity", "活动记录"],
  ["Administration", "用户管理"],
  ["Account", "账户"],

  ["Search", "搜索"],
  ["Search pages", "搜索页面"],
  ["Sign in", "登录"],
  ["Sign out", "退出登录"],
  ["Work email", "工作邮箱"],
  ["Password", "密码"],
  ["Loading...", "加载中..."],
  ["Loading…", "加载中…"],

  ["Total leads", "线索总数"],
  ["Usable email", "有效邮箱"],
  ["Decision contacts", "决策联系人"],
  ["High-priority leads", "高优先级线索"],
  ["GIS coverage", "地图覆盖率"],
  ["Lead-campaign reach", "客户开发触达"],
  ["Data quality at a glance", "数据质量概览"],
  ["Ready leads", "就绪线索"],
  ["Portfolio", "线索总库"],
  ["Company email coverage", "公司邮箱覆盖率"],
  ["Verified email coverage", "已验证邮箱覆盖率"],
  ["Decision-contact coverage", "决策联系人覆盖率"],

  ["Stock intelligence", "库存智能"],
  ["MONTHLY MOVEMENT", "月度变化"],
  ["TOTAL STOCK", "库存总量"],
  ["STOCK ABOVE 20 M²", "20 m² 以上库存"],
  ["Stock comparison", "库存对比"],
  ["Total stock", "库存总量"],
  ["Leads reached", "触达线索"],
  ["Countries", "国家"],
  ["Emails", "邮件"],
  ["this month", "本月"],
  ["reached", "已触达"],
  ["stock outreach", "库存推广"],
  ["ready now", "当前就绪"],
  ["models", "个型号"],

  ["Available Leads", "可认领线索"],
  ["Available leads", "可认领线索"],
  ["Claimed Leads", "已认领线索"],
  ["Claimed leads", "已认领线索"],
  ["Claim Lead", "认领线索"],
  ["Claim lead", "认领线索"],
  ["Claim", "认领"],
  ["Release", "释放"],
  ["Released", "已释放"],
  ["Claimed", "已认领"],
  ["Lead ID", "线索 ID"],
  ["Company", "公司"],
  ["Country", "国家"],
  ["City", "城市"],
  ["State", "州/省"],
  ["Address", "地址"],
  ["Phone", "电话"],
  ["Website", "网站"],
  ["Email", "邮箱"],
  ["Contact Name", "联系人姓名"],
  ["Contact name", "联系人姓名"],
  ["Contact", "联系人"],
  ["Job Title", "职位"],
  ["Job title", "职位"],
  ["Status", "状态"],
  ["Market", "市场"],
  ["Source", "来源"],
  ["Score", "评分"],
  ["Priority", "优先级"],
  ["Assigned To", "负责人"],
  ["Assigned to", "负责人"],
  ["Assigned At", "认领时间"],
  ["Assigned at", "认领时间"],
  ["Actions", "操作"],
  ["View", "查看"],
  ["Edit", "编辑"],
  ["Save", "保存"],
  ["Cancel", "取消"],
  ["Close", "关闭"],
  ["Apply", "应用"],
  ["Reset", "重置"],
  ["Filter", "筛选"],
  ["Filters", "筛选条件"],
  ["Clear filters", "清除筛选"],
  ["Export", "导出"],
  ["Download", "下载"],
  ["Refresh", "刷新"],
  ["All Countries", "所有国家"],
  ["All countries", "所有国家"],
  ["Available", "可认领"],
  ["Unassigned", "未分配"],

  ["Add Note", "添加备注"],
  ["Add note", "添加备注"],
  ["Your Notes", "我的备注"],
  ["Your notes", "我的备注"],
  ["Notes", "备注"],
  ["Note", "备注"],
  ["Add Follow-up", "添加跟进"],
  ["Add follow-up", "添加跟进"],
  ["Create Follow-up", "创建跟进"],
  ["Create follow-up", "创建跟进"],
  ["Follow-up Date", "跟进日期"],
  ["Follow-up date", "跟进日期"],
  ["Follow-up", "跟进"],
  ["Due Date", "到期日期"],
  ["Due date", "到期日期"],
  ["Completed", "已完成"],
  ["Pending", "待处理"],
  ["Complete", "完成"],
  ["Title", "标题"],
  ["Description", "描述"],

  ["Lead ownership", "线索认领情况"],
  ["Current workload and lead claim history", "当前工作量与线索认领记录"],
  ["Current claimed leads by team member", "团队成员当前认领线索"],
  ["Claim history", "认领记录"],
  ["Team Member", "团队成员"],
  ["Team member", "团队成员"],
  ["Role", "角色"],
  ["Current Leads", "当前线索数"],
  ["Current leads", "当前线索数"],
  ["Last Claimed", "最近认领时间"],
  ["Last claimed", "最近认领时间"],
  ["Time", "时间"],
  ["Action", "操作"],

  ["Campaign Control", "活动控制"],
  ["Campaign control", "活动控制"],
  ["Lead Outreach", "客户开发"],
  ["Lead outreach", "客户开发"],
  ["Stock Promotion", "库存推广"],
  ["Stock promotion", "库存推广"],
  ["ACTIVE", "运行中"],
  ["INACTIVE", "已停用"],
  ["Agent Enabled", "启用自动发送"],
  ["Agent enabled", "启用自动发送"],
  ["Target Countries", "目标国家"],
  ["Target countries", "目标国家"],
  ["Start Date", "开始日期"],
  ["Start date", "开始日期"],
  ["End Date", "结束日期"],
  ["End date", "结束日期"],
  ["Save Controls", "保存设置"],
  ["Save controls", "保存设置"],
  ["Sent This Month", "本月已发送"],
  ["Sent this month", "本月已发送"],
  ["Unique Leads Reached", "触达线索数"],
  ["Unique leads reached", "触达线索数"],
  ["Countries Reached", "触达国家数"],
  ["Countries reached", "触达国家数"],
  ["Failed", "失败"],
  ["Last Send", "最近发送"],
  ["Last send", "最近发送"],
  ["Sender", "发件账号"],
  ["Manual stock promotion", "手动库存推广"],
  ["Select lead manually", "手动选择线索"],
  ["Search company, contact or email", "搜索公司、联系人或邮箱"],
  ["Send with agent", "由智能代理发送"],
  ["Send without waiting for local-time window", "无需等待客户当地发送时段"],
  ["Working…", "处理中…"],
  ["Cancel", "取消"],
  ["No named contact", "无实名联系人"],
  ["No email", "无邮箱"],

  ["Current Stock", "当前库存"],
  ["Current stock", "当前库存"],
  ["Current Inventory", "当前库存"],
  ["Current inventory", "当前库存"],
  ["Product Type", "产品类型"],
  ["Product type", "产品类型"],
  ["Product", "产品"],
  ["Model", "型号"],
  ["Color", "颜色"],
  ["Thickness", "厚度"],
  ["Wear Layer", "耐磨层"],
  ["Wear layer", "耐磨层"],
  ["Size", "尺寸"],
  ["Quantity", "数量"],
  ["Area (m²)", "面积（m²）"],
  ["Area", "面积"],
  ["Price", "价格"],
  ["RMB", "人民币"],
  ["Inventory", "库存"],
  ["Models", "型号数"],
  ["Total Area", "总面积"],
  ["Total area", "总面积"],

  ["Data Quality Issues", "数据质量问题"],
  ["Data quality issues", "数据质量问题"],
  ["Missing Email", "缺少邮箱"],
  ["Missing email", "缺少邮箱"],
  ["Missing Contact", "缺少联系人"],
  ["Missing contact", "缺少联系人"],
  ["Missing Phone", "缺少电话"],
  ["Missing phone", "缺少电话"],
  ["Missing Website", "缺少网站"],
  ["Missing website", "缺少网站"],
  ["Missing Coordinates", "缺少坐标"],
  ["Missing coordinates", "缺少坐标"],
  ["Unverified Email", "未验证邮箱"],
  ["Unverified email", "未验证邮箱"],
  ["Duplicate Domains", "重复域名"],
  ["Duplicate domains", "重复域名"],

  ["Users", "用户"],
  ["User", "用户"],
  ["Full Name", "姓名"],
  ["Full name", "姓名"],
  ["Active", "启用"],
  ["Inactive", "停用"],
  ["Sales Manager", "销售经理"],
  ["Sales Representative", "销售代表"],
  ["Business Assistant", "业务助理"],
  ["BI Admin", "BI 管理员"],
  ["Administrator", "管理员"],
  ["Manage user access and assigned roles.", "管理用户访问权限与角色。"],
  ["Manage JCL users and access levels.", "管理 JCL 用户与访问级别。"],
  ["Access is based on your assigned role.", "访问权限由您的角色决定。"],

  ["Latest operational events", "最新业务活动"],
  ["newest activity appears first", "最新活动优先显示"],
  ["No activity records available.", "暂无活动记录。"],
  ["No activity records available", "暂无活动记录"],

  ["Generate Report", "生成报告"],
  ["Generate report", "生成报告"],
  ["Print / Save PDF", "打印 / 保存 PDF"],
  ["Lead Performance", "线索表现"],
  ["Email Coverage", "邮箱覆盖率"],
  ["Usable Email Coverage", "有效邮箱覆盖率"],
  ["Lead Campaign Reach", "客户开发触达"],
  ["Stock Campaign Reach", "库存推广触达"],
  ["Key Insights", "关键洞察"],
  ["Generated On", "生成时间"],
  ["Period", "期间"],
  ["New replies", "新回复"],
  ["New reply", "新回复"],
  ["No new replies.", "暂无新回复。"],
  ["No recent qualified client replies.", "暂无近期合格客户回复。"],
  ["Recent replies", "近期回复"],
  ["Client response hidden", "客户回复已隐藏"],
  ["Reply content is visible to authorized management only", "回复内容仅授权管理人员可见"],

  // Shared navigation, page headers and lead workspace.
  ["Sales workspace", "销售工作区"],
  ["Inventory intelligence", "库存智能"],
  ["Analysis & control", "分析与控制"],
  ["Super Admin", "超级管理员"],
  ["Internal sales intelligence", "内部销售智能"],
  ["Search, qualify and assign the JCL lead portfolio", "搜索、筛选并分配 JCL 潜在客户"],
  ["Your claimed accounts and active sales work", "您已认领的客户及进行中的销售工作"],
  ["Open actions, due dates and sales commitments", "待办事项、到期日期与销售承诺"],
  ["Current inventory, readiness and stock pressure", "当前库存、就绪度与库存压力"],
  ["Outbound stock promotion activity and reach", "库存推广活动与触达情况"],
  ["Geographic distribution of qualified sales opportunities", "合格销售机会的地理分布"],
  ["Coverage, completeness and lead readiness controls", "覆盖率、完整性与潜在客户就绪度控制"],
  ["Operational events across the JCL sales workflow", "JCL 销售流程中的业务活动"],
  ["Users, access and application controls", "用户、访问权限与应用控制"],
  ["Management-ready lead, campaign and stock reporting", "面向管理层的潜在客户、活动与库存报告"],
  ["Profile and effective application permissions", "个人资料与实际应用权限"],
  ["Lead Database", "潜在客户库"],
  ["Lead database", "潜在客户库"],
  ["My Leads", "我的潜在客户"],
  ["My leads", "我的潜在客户"],
  ["Owned accounts, notes, next actions and follow-up discipline.", "已认领客户、备注、下一步行动与跟进管理。"],
  ["Super Admin view: full lead database, full contact details and ownership control.", "超级管理员视图：完整潜在客户库、完整联系信息及归属控制。"],
  ["Only company name and country are visible. Claim a lead to unlock its contact details.", "仅显示公司名称和国家。认领潜在客户后可查看联系信息。"],
  ["Search qualified companies, inspect contact quality and claim accounts atomically.", "搜索合格公司、检查联系人质量并安全认领客户。"],
  ["Owned accounts", "已认领客户"],
  ["All leads", "全部潜在客户"],
  ["Visible leads", "可见潜在客户"],
  ["Contact details", "联系信息"],
  ["to unlock one lead", "以解锁一个潜在客户"],
  ["Usable email", "有效邮箱"],
  ["GIS ready", "GIS 已就绪"],
  ["High-priority", "高优先级"],
  ["B2B score threshold", "B2B 评分阈值"],
  ["Qualified lead inventory", "合格潜在客户库"],
  ["EXPORT FILTERED", "导出筛选结果"],
  ["entries per page", "每页条目数"],
  ["All email states", "所有邮箱状态"],
  ["Search company or country", "搜索公司或国家"],
  ["Search company or contact", "搜索公司或联系人"],
  ["Not enriched", "未富集"],
  ["my lead", "我的潜在客户"],
  ["Loading your lead portfolio…", "正在加载您的潜在客户组合…"],
  ["Loading full lead database…", "正在加载完整潜在客户库…"],
  ["Loading available leads…", "正在加载可认领潜在客户…"],

  // Follow-ups.
  ["Follow-up completed.", "跟进已完成。"],
  ["next action", "下一步行动"],
  ["Loading follow-up schedule…", "正在加载跟进计划…"],
  ["needs attention", "需要处理"],
  ["Due today", "今日到期"],
  ["future open actions", "未来待办"],
  ["closed actions", "已关闭事项"],

  // Dashboard.
  ["Lead campaign reach", "潜在客户活动触达"],
  ["Stock campaign reach", "库存活动触达"],
  ["Previous stock", "上期库存"],
  ["Latest stock summary compared with the previous approved detailed snapshot", "最新库存汇总与上一已批准详细快照对比"],
  ["Current month compared with the previous approved snapshot", "本月与上一已批准快照对比"],
  ["Luxury Vinyl Tile", "豪华乙烯基地板"],
  ["Previous approved snapshot not available", "暂无上一已批准快照"],
  ["Current stock by product family, all reported above the 20 m² threshold", "按产品系列显示当前库存，所有型号均高于 20 m² 阈值"],
  ["Inventory level, promotion readiness and market reach", "库存水平、推广就绪度与市场触达"],
  ["BASELINE PENDING", "等待基准"],
  ["Largest stock family", "最大库存系列"],
  ["No stock mix data", "暂无库存结构数据"],
  ["Inventory with no stock-email exposure", "未进行库存邮件推广的库存"],
  ["Share of current stock sitting in families with zero recorded stock emails", "当前库存中尚无库存邮件记录的产品系列占比"],
  ["Email concentration", "邮件集中度"],
  ["No stock-email activity recorded", "暂无库存邮件活动记录"],
  ["Average stock per model", "每个型号平均库存"],
  ["Exposed, no interest yet", "已触达，尚无兴趣"],
  ["No campaign exposure", "尚未触达"],
  ["Sales-ready leads", "销售就绪潜在客户"],
  ["Target-fit backlog", "目标匹配待处理"],
  ["Relevant leads still blocked by contact-data gaps", "相关潜在客户仍受联系人数据缺口影响"],
  ["Score validation sample", "评分验证样本"],
  ["Contacted leads available to test whether B2B score predicts outcomes", "用于检验 B2B 评分是否能预测结果的已联系潜在客户"],
  ["Loading JCL intelligence…", "正在加载 JCL 智能数据…"],

  // Stock.
  ["RMB (CNY)", "人民币（CNY）"],
  ["US dollar (USD)", "美元（USD）"],
  ["Euro (EUR)", "欧元（EUR）"],
  ["SKU / product", "SKU / 产品"],
  ["No SKU", "无 SKU"],
  ["not set", "未设置"],
  ["not eligible", "不符合条件"],
  ["Review / edit", "查看 / 编辑"],
  ["Stock item updated.", "库存项目已更新。"],
  ["Monthly stock snapshot captured.", "月度库存快照已保存。"],
  ["Confirm current SKU availability", "确认当前 SKU 可用性"],
  ["No confirmed export price rows are available for this selection.", "当前选择没有已确认的出口价格记录。"],
  ["Please allow pop-ups for this site to open the client price list.", "请允许此网站弹出窗口以打开客户价格表。"],
  ["Loading current inventory…", "正在加载当前库存…"],
  ["Stock portfolio", "库存组合"],
  ["Current approved inventory, promotion eligibility, exact pricing and month-on-month stock movement.", "当前已批准库存、推广资格、准确价格及月度库存变化。"],
  ["Product families", "产品系列"],
  ["Change vs previous", "较上期变化"],
  ["No baseline", "无基准"],
  ["Previous approved snapshot required", "需要上一已批准快照"],
  ["Detailed inventory", "详细库存"],
  ["Pricing readiness", "定价就绪度"],
  ["Current SKU-level file required for live exact-lot sending", "实时精准批次发送需要当前 SKU 级库存文件"],
  ["no comparable baseline", "无可比基准"],
  ["not identified", "未识别"],
  ["Search SKU or format", "搜索 SKU 或规格"],
  ["Price-list currency", "价格表币种"],
  ["Review stock item", "查看库存项目"],
  ["Stock item detail", "库存项目详情"],
  ["Price status", "价格状态"],
  ["Target price", "目标价格"],
  ["Price unit", "价格单位"],
  ["No internal eligibility note.", "暂无内部资格备注。"],
  ["Save changes", "保存更改"],

  // Campaigns.
  ["No date limit", "无日期限制"],
  ["Add another country", "添加国家"],
  ["All eligible countries", "所有符合条件的国家"],
  ["Empty means all eligible countries", "留空表示所有符合条件的国家"],
  ["Country priority (optional)", "国家优先级（可选）"],
  ["Add next priority country", "添加下一个优先国家"],
  ["No country priority", "未设置国家优先级"],
  ["First selected ranks highest. Priority does not restrict the target countries above.", "第一个国家优先级最高。优先级不会限制上方的目标国家范围。"],
  ["Empty means no country priority; leads are ranked by qualification and contact quality.", "留空表示不设置国家优先级；按客户资质及联系人质量排序。"],
  ["No recorded send", "暂无发送记录"],
  ["This lead is already queued for the outreach agent.", "该潜在客户已在开发代理队列中。"],
  ["Selected lead queued. The lead-outreach agent will process this exact lead before automatic candidates.", "已加入所选潜在客户。开发代理会在自动候选之前处理该客户。"],
  ["Could not queue this lead for lead outreach.", "无法将该潜在客户加入开发队列。"],
  ["Queued manual lead-outreach send cancelled.", "已取消手动开发队列发送。"],
  ["Could not cancel this queued lead.", "无法取消该队列中的潜在客户。"],
  ["Timing override saved for this queued lead.", "已保存该队列潜在客户的时间覆盖设置。"],
  ["No country priority is configured. Named decision-makers with a direct email or phone are ranked by lead quality.", "未设置国家优先级。有直接邮箱或电话的实名决策人将按潜在客户质量排序。"],
  ["Email sends still keep the existing DNC, cooldown, website, email/MX and daily-limit safeguards.", "邮件发送仍保留现有的 DNC、冷却期、网站、邮箱/MX 和每日限额保护。"],
  ["Select lead manually", "手动选择潜在客户"],
  ["Queue agent send", "加入代理发送队列"],
  ["Not named", "未实名"],
  ["immediate window override", "立即发送时段覆盖"],
  ["local business hours", "当地工作时间"],
  ["This lead is already queued for the stock agent.", "该潜在客户已在库存代理队列中。"],
  ["Selected lead queued. The stock agent will process this exact lead before automatic candidates.", "已加入所选潜在客户。库存代理会在自动候选之前处理该客户。"],
  ["Could not queue this lead.", "无法将该潜在客户加入队列。"],
  ["Queued manual send cancelled.", "已取消手动发送队列。"],
  ["Timing override saved for this queued stock lead.", "已保存该库存队列潜在客户的时间覆盖设置。"],
  ["Loading campaign controls…", "正在加载活动控制…"],

  // GIS.
  ["GIS map renderer failed; switching to safe geographic fallback.", "GIS 地图渲染失败，正在切换到安全地理回退视图。"],
  ["Geographic lead plot", "潜在客户地理分布图"],
  ["Unknown company", "未知公司"],
  ["Loading GIS lead portfolio…", "正在加载 GIS 潜在客户组合…"],
  ["GIS opportunity map", "GIS 商机地图"],
  ["Company locations", "公司位置"],
  ["duplicate-location contact pins consolidated", "已合并重复位置的联系人标记"],
  ["B2B score · lead records", "B2B 评分 · 潜在客户记录"],
  ["MapLibre GIS error; switching to fallback.", "MapLibre GIS 出错，正在切换到回退视图。"],

  // Data quality.
  ["Marketing data intelligence", "营销数据智能"],
  ["Descriptive patterns that explain portfolio structure, sales readiness, market concentration and whether lead scoring is producing better commercial outcomes.", "用于解释客户组合结构、销售就绪度、市场集中度以及潜在客户评分是否带来更好商业结果的描述性模式。"],
  ["Building commercial data patterns…", "正在构建商业数据模式…"],
  ["Target-fit leads", "目标匹配潜在客户"],
  ["Sales-ready within target-fit", "目标匹配中的销售就绪客户"],
  ["Commercially relevant leads still needing enrichment", "仍需富集的商业相关潜在客户"],
  ["Qualified-response baseline", "合格回复基准"],
  ["Market concentration", "市场集中度"],
  ["Decision-contact depth", "决策联系人深度"],
  ["Email verification yield", "邮箱验证产出"],
  ["Score concentration", "评分集中度"],
  ["Largest unresolved market", "最大未解决市场"],
  ["No market backlog available.", "暂无市场待处理数据。"],
  ["Best observed market readiness", "最佳观察市场就绪度"],
  ["Not enough market-level observations yet.", "暂无足够的市场级观察数据。"],
  ["Human replies", "人工回复"],
  ["Qualified rate", "合格率"],
  ["not enough contacted leads yet", "已联系潜在客户数量不足"],
  ["Target fit", "目标匹配"],
  ["Sales ready", "销售就绪"],

  // Reports.
  ["Lead intelligence", "潜在客户智能"],
  ["Daily unique leads reached", "每日独立触达潜在客户"],
  ["Email quality", "邮箱质量"],
  ["Contactable portfolio share", "可联系客户组合占比"],
  ["Lead quality trend", "潜在客户质量趋势"],
  ["Six-month contactability and priority movement", "六个月可联系性与优先级变化"],
  ["Send-readiness progression", "发送就绪度进展"],
  ["How many leads survive each readiness gate", "各就绪度关卡保留的潜在客户数量"],
  ["Data-quality priorities", "数据质量优先事项"],
  ["Largest gaps to correct next", "下一步需修复的最大缺口"],
  ["Stock movement", "库存变化"],
  ["Current vs previous approved snapshot", "当前库存与上一已批准快照对比"],
  ["Stock composition", "库存构成"],
  ["Current inventory by product family", "按产品系列显示当前库存"],
  ["Current product stock", "当前产品库存"],
  ["Promotion-ready inventory by family", "按系列显示推广就绪库存"],
  ["Management interpretation", "管理层解读"],
  ["Automatic interpretation of the selected report sections", "所选报告部分的自动解读"],
  ["Loading reporting data…", "正在加载报告数据…"],

  // Activity / Admin / Account.
  ["Loading activity history…", "正在加载活动历史…"],
  ["Activity log", "活动日志"],
  ["Auditable ownership, campaign, stock and follow-up events across the JCL sales workflow.", "JCL 销售流程中的可审计归属、活动、库存及跟进事件。"],
  ["JCL record", "JCL 记录"],
  ["User role updated.", "用户角色已更新。"],
  ["Unnamed user", "未命名用户"],
  ["Loading application users…", "正在加载应用用户…"],
  ["Users and access", "用户与访问权限"],
  ["Current identity, role and effective application permissions.", "当前身份、角色及实际应用权限。"],
  ["Claim and manage leads", "认领并管理潜在客户"],
  ["Stock and campaign analytics", "库存与活动分析"],
  ["Edit stock pricing", "编辑库存价格"],
  ["Manage users", "管理用户"],
  ["Expand navigation", "展开导航"],
  ["Collapse navigation", "收起导航"],
  ["JCL User", "JCL 用户"],
];

const EN_TO_ZH_CN = new Map(TRANSLATIONS);
const EN_LOWER_TO_ZH_CN = new Map(
  TRANSLATIONS.map(([english, chinese]) => [english.toLowerCase(), chinese])
);

const TAIWAN_TERM_REPLACEMENTS = [
  ["仪表盘", "儀表板"],
  ["智能平台", "智慧平台"],
  ["线索", "潛在客戶"],
  ["邮箱", "電子郵件"],
  ["联系人", "聯絡人"],
  ["用户", "使用者"],
  ["数据", "資料"],
  ["跟进", "追蹤"],
  ["质量", "品質"],
  ["账户", "帳戶"],
  ["登录", "登入"],
  ["退出登录", "登出"],
  ["库存", "庫存"],
  ["推广", "推廣"],
  ["报告", "報告"],
  ["地图", "地圖"],
];

const TRADITIONAL_CHAR_MAP = {
  "与": "與", "为": "為", "仅": "僅", "从": "從", "个": "個", "们": "們",
  "优": "優", "会": "會", "传": "傳", "体": "體", "价": "價", "关": "關",
  "决": "決", "况": "況", "准": "準", "创": "創", "删": "刪", "则": "則",
  "办": "辦", "务": "務", "动": "動", "区": "區", "单": "單", "卖": "賣",
  "厂": "廠", "历": "歷", "参": "參", "发": "發", "变": "變", "号": "號",
  "启": "啟", "员": "員", "响": "響", "国": "國", "图": "圖", "块": "塊",
  "处": "處", "备": "備", "复": "複", "学": "學", "实": "實", "审": "審",
  "导": "導", "对": "對", "寻": "尋", "将": "將", "层": "層", "属": "屬",
  "币": "幣", "师": "師", "帐": "帳", "广": "廣", "库": "庫", "应": "應",
  "开": "開", "归": "歸", "当": "當", "录": "錄", "态": "態", "总": "總",
  "户": "戶", "执": "執", "扩": "擴", "扫": "掃", "扬": "揚", "报": "報",
  "拟": "擬", "换": "換", "据": "據", "数": "數", "无": "無", "旧": "舊",
  "时": "時", "显": "顯", "术": "術", "机": "機", "权": "權", "条": "條",
  "来": "來", "极": "極", "构": "構", "标": "標", "样": "樣", "检": "檢",
  "楼": "樓", "欢": "歡", "气": "氣", "测": "測", "湾": "灣", "满": "滿",
  "热": "熱", "爱": "愛", "独": "獨", "现": "現", "电": "電", "监": "監",
  "盘": "盤", "确": "確", "礼": "禮", "种": "種", "称": "稱", "积": "積",
  "稳": "穩", "竞": "競", "笔": "筆", "筛": "篩", "简": "簡", "类": "類",
  "紧": "緊", "级": "級", "约": "約", "纪": "紀", "纳": "納", "纸": "紙",
  "纹": "紋", "线": "線", "组": "組", "细": "細", "经": "經", "结": "結",
  "给": "給", "络": "絡", "统": "統", "继": "繼", "续": "續", "维": "維",
  "编": "編", "网": "網", "罗": "羅", "职": "職", "联": "聯", "胜": "勝",
  "胶": "膠", "艺": "藝", "节": "節", "获": "獲", "营": "營", "虑": "慮",
  "虽": "雖", "补": "補", "装": "裝", "见": "見", "观": "觀", "规": "規",
  "视": "視", "览": "覽", "触": "觸", "计": "計", "订": "訂", "认": "認",
  "议": "議", "讯": "訊", "记": "記", "讲": "講", "许": "許", "论": "論",
  "设": "設", "访": "訪", "证": "證", "评": "評", "该": "該", "详": "詳",
  "语": "語", "说": "說", "请": "請", "读": "讀", "调": "調", "谢": "謝",
  "贝": "貝", "负": "負", "财": "財", "责": "責", "货": "貨", "质": "質",
  "购": "購", "贵": "貴", "贸": "貿", "费": "費", "资": "資", "赛": "賽",
  "趋": "趨", "车": "車", "转": "轉", "轮": "輪", "软": "軟", "较": "較",
  "边": "邊", "达": "達", "过": "過", "运": "運", "还": "還", "这": "這",
  "进": "進", "远": "遠", "连": "連", "选": "選", "递": "遞", "邮": "郵",
  "释": "釋", "锁": "鎖", "销": "銷", "错": "錯", "长": "長", "门": "門",
  "闭": "閉", "问": "問", "间": "間", "阅": "閱", "队": "隊", "阶": "階",
  "际": "際", "隐": "隱", "难": "難", "静": "靜", "顾": "顧", "预": "預",
  "领": "領", "频": "頻", "题": "題", "额": "額", "风": "風", "驱": "驅",
  "验": "驗", "鲜": "鮮", "齐": "齊", "龙": "龍",
};

function toTaiwanChinese(value) {
  let result = String(value || "");
  TAIWAN_TERM_REPLACEMENTS.forEach(([simplified, taiwan]) => {
    result = result.split(simplified).join(taiwan);
  });
  return [...result].map((char) => TRADITIONAL_CHAR_MAP[char] || char).join("");
}

const EN_TO_ZH_TW = new Map(
  TRANSLATIONS.map(([english, chinese]) => [english, toTaiwanChinese(chinese)])
);

const TAIWAN_EXACT_OVERRIDES = new Map([
  ["Lead Database", "潛在客戶資料庫"],
  ["Lead database", "潛在客戶資料庫"],
  ["My Leads", "我的潛在客戶"],
  ["My leads", "我的潛在客戶"],
  ["All leads", "全部潛在客戶"],
  ["Visible leads", "可見潛在客戶"],
  ["Qualified lead inventory", "合格潛在客戶資料庫"],
  ["Available Leads", "可認領潛在客戶"],
  ["Available leads", "可認領潛在客戶"],
  ["Claimed Leads", "已認領潛在客戶"],
  ["Claimed leads", "已認領潛在客戶"],
  ["Lead ID", "潛在客戶 ID"],
  ["Claim Lead", "認領潛在客戶"],
  ["Claim lead", "認領潛在客戶"],
  ["Search, qualify and assign the JCL lead portfolio", "搜尋、篩選並分配 JCL 潛在客戶"],
  ["Super Admin", "超級管理員"],
  ["Sales workspace", "銷售工作區"],
  ["Inventory intelligence", "庫存智慧"],
  ["Analysis & control", "分析與控制"],
]);

TAIWAN_EXACT_OVERRIDES.forEach((localized, english) => {
  EN_TO_ZH_TW.set(english, localized);
});

const EN_LOWER_TO_ZH_TW = new Map(
  [...EN_TO_ZH_TW.entries()].map(([english, chinese]) => [english.toLowerCase(), chinese])
);

const DYNAMIC_PATTERNS = [
  {
    en: /^([\d,]+) available · ([\d,]+) countries$/,
    zh: /^([\d,]+) 可认领 · ([\d,]+) 个国家$/,
    toZh: (match) => `${match[1]} 可认领 · ${match[2]} 个国家`,
    toEn: (match) => `${match[1]} available · ${match[2]} countries`,
  },
  {
    en: /^([\d,]+) leads are contactable$/,
    zh: /^([\d,]+) 条线索可联系$/,
    toZh: (match) => `${match[1]} 条线索可联系`,
    toEn: (match) => `${match[1]} leads are contactable`,
  },
  {
    en: /^([\d,]+) named decision-makers$/,
    zh: /^([\d,]+) 位已识别决策人$/,
    toZh: (match) => `${match[1]} 位已识别决策人`,
    toEn: (match) => `${match[1]} named decision-makers`,
  },
  {
    en: /^([\d,]+) mapped records$/,
    zh: /^([\d,]+) 条已定位记录$/,
    toZh: (match) => `${match[1]} 条已定位记录`,
    toEn: (match) => `${match[1]} mapped records`,
  },
  {
    en: /^([\d,]+) countries reached this month$/,
    zh: /^本月已触达 ([\d,]+) 个国家$/,
    toZh: (match) => `本月已触达 ${match[1]} 个国家`,
    toEn: (match) => `${match[1]} countries reached this month`,
  },
  {
    en: /^([\d,]+) leads have an email$/,
    zh: /^([\d,]+) 条线索有邮箱$/,
    toZh: (match) => `${match[1]} 条线索有邮箱`,
    toEn: (match) => `${match[1]} leads have an email`,
  },
  {
    en: /^([\d,]+) verified contacts$/,
    zh: /^([\d,]+) 个已验证联系人$/,
    toZh: (match) => `${match[1]} 个已验证联系人`,
    toEn: (match) => `${match[1]} verified contacts`,
  },
  {
    en: /^([\d,]+) leads have a named buyer or decision-maker$/,
    zh: /^([\d,]+) 条线索已有明确采购人或决策人$/,
    toZh: (match) => `${match[1]} 条线索已有明确采购人或决策人`,
    toEn: (match) => `${match[1]} leads have a named buyer or decision-maker`,
  },
];

function sanitizeTechnicalText(value) {
  let result = value;

  SAFE_TEXT_REPLACEMENTS.forEach(([technicalText, businessText]) => {
    result = result.split(technicalText).join(businessText);
  });

  result = result.replace(
    /Updating application profile:[^|]*\|\s*PGRST\d+/gi,
    "Profile update failed. Please try again."
  );

  result = result.replace(
    /Could not find the ['"]updated_at['"] column of ['"]app_profiles['"][^|]*\|\s*PGRST\d+/gi,
    "Profile update failed. Please try again."
  );

  return result;
}

const EXTRA_DYNAMIC_PATTERNS = [
  {
    en: /^([\d,.]+)% of the portfolio can be worked now$/,
    toZh: (match) => `当前可开展业务的客户占比 ${match[1]}%`,
  },
  {
    en: /^([\d,.]+)% of the portfolio is commercially eligible$/,
    toZh: (match) => `符合商业条件的客户占比 ${match[1]}%`,
  },
  {
    en: /^([\d,]+) leads can be worked now$/,
    toZh: (match) => `当前可开展业务的线索：${match[1]} 条`,
  },
  {
    en: /^([\d,]+) contacted leads(?: in the current validation sample| — early sample)$/,
    toZh: (match) => `当前验证样本中已联系线索：${match[1]} 条`,
  },
  {
    en: /^([\d,]+) models$/,
    toZh: (match) => `${match[1]} 个型号`,
  },
  {
    en: /^([\d,.]+)% of current stock$/,
    toZh: (match) => `占当前库存 ${match[1]}%`,
  },
  {
    en: /^([\d,]+) models across the displayed product families$/,
    toZh: (match) => `当前显示产品系列共 ${match[1]} 个型号`,
  },
  {
    en: /^([\d,]+) interested$/,
    toZh: (match) => `${match[1]} 个有兴趣`,
  },
  {
    en: /^([\d,]+) selected$/,
    toZh: (match) => `已选择 ${match[1]} 个`,
  },
  {
    en: /^([\d,]+) active$/,
    toZh: (match) => `${match[1]} 个进行中`,
  },
  {
    en: /^([\d,]+) models above 20 m²$/,
    toZh: (match) => `20 m² 以上型号：${match[1]} 个`,
  },
  {
    en: /^([\d,]+) lots in active snapshot$/,
    toZh: (match) => `当前库存快照批次：${match[1]} 个`,
  },
  {
    en: /^([\d,]+) eligible export lots$/,
    toZh: (match) => `符合条件的出口批次：${match[1]} 个`,
  },
  {
    en: /^([\d,]+) lots have confirmed campaign price$/,
    toZh: (match) => `已确认推广价格的批次：${match[1]} 个`,
  },
  {
    en: /^([\d,]+) named contacts$/,
    toZh: (match) => `实名联系人：${match[1]} 位`,
  },
  {
    en: /^([\d,]+) contactable$/,
    toZh: (match) => `可联系：${match[1]} 条`,
  },
  {
    en: /^([\d,]+) countries$/,
    toZh: (match) => `${match[1]} 个国家`,
  },
  {
    en: /^([\d,]+) leads$/,
    toZh: (match) => `${match[1]} 条线索`,
  },
  {
    en: /^([\d,.]+) boxes$/,
    toZh: (match) => `${match[1]} 箱`,
  },
  {
    en: /^· ([\d.]+) mm wear layer$/,
    toZh: (match) => `· ${match[1]} mm 耐磨层`,
  },
  {
    en: /^([\d.]+) mm wear$/,
    toZh: (match) => `${match[1]} mm 耐磨层`,
  },
  {
    en: /^([\d,]+) countries selected; clear for worldwide outreach\.$/,
    toZh: (match) => `已选择 ${match[1]} 个目标国家；清空后恢复全球开发。`,
  },
  {
    en: /^Country priority: (.+)\. Only named decision-makers with a direct email or phone are listed\.$/,
    toZh: (match) => `国家优先级：${match[1]}。仅显示有直接邮箱或电话的实名决策人。`,
  },
  {
    en: /^Lead portfolio: ([\d,]+) records across ([\d,]+) countries, with ([\d.]+)% usable-email coverage\.$/,
    toZh: (match) => `线索组合：共 ${match[1]} 条记录，覆盖 ${match[2]} 个国家，有效邮箱覆盖率为 ${match[3]}%。`,
  },
  {
    en: /^Lead outreach reached ([\d,]+) unique lead\/day touchpoints during the selected period\.$/,
    toZh: (match) => `所选期间客户开发触达 ${match[1]} 个独立线索/日触点。`,
  },
  {
    en: /^Stock outreach reached ([\d,]+) unique lead\/day touchpoints during the selected period\.$/,
    toZh: (match) => `所选期间库存推广触达 ${match[1]} 个独立线索/日触点。`,
  },
  {
    en: /^Decision-contact coverage is ([\d.]+)%; enrichment-ready is ([\d.]+)% and verified send-ready is ([\d.]+)%\.$/,
    toZh: (match) =>
      `决策联系人覆盖率为 ${match[1]}%；可富集比例为 ${match[2]}%，已验证可发送比例为 ${match[3]}%。`,
  },
  {
    en: /^Current stock is ([\d,.]+) m² across ([\d,]+) models\. No approved comparison baseline is available\.$/,
    toZh: (match) => `当前库存为 ${match[1]} m²，共 ${match[2]} 个型号。暂无已批准的对比基准。`,
  },
  {
    en: /^Current stock is ([\d,.]+) m² across ([\d,]+) models\. Inventory is (up|down) ([\d.]+)% versus the previous approved snapshot\.$/,
    toZh: (match) =>
      `当前库存为 ${match[1]} m²，共 ${match[2]} 个型号。较上一已批准快照${match[3] === "up" ? "上升" : "下降"} ${match[4]}%。`,
  },
  {
    en: /^(.+) receives this share of stock-email activity$/,
    toZh: (match) => `${match[1]} 占库存推广邮件活动的这一比例`,
  },
  {
    en: /^Top 5 markets hold this share of all leads(?: · (.+))?$/,
    toZh: (match) =>
      `前 5 大市场占全部线索的这一比例${match[1] ? ` · ${match[1]}` : ""}`,
  },
  {
    en: /^score ([\d.]+)$/i,
    toZh: (match) => `评分 ${match[1]}`,
  },
  {
    en: /^([\d,]+) countries in current view$/,
    toZh: (match) => `当前视图包含 ${match[1]} 个国家`,
  },
  {
    en: /^([\d.]+)% coverage$/,
    toZh: (match) => `覆盖率 ${match[1]}%`,
  },
  {
    en: /^([\d.]+)% with coordinates$/,
    toZh: (match) => `已有坐标 ${match[1]}%`,
  },
  {
    en: /^([\d+]+) B2B score threshold$/,
    toZh: (match) => `B2B 评分阈值 ${match[1]}`,
  },
  {
    en: /^([\d,]+) records match the current filters$/,
    toZh: (match) => `当前筛选条件匹配 ${match[1]} 条记录`,
  },
  {
    en: /^([\d,]+) of ([\d,]+) existing emails are verified\/high-confidence\.$/,
    toZh: (match) => `现有 ${match[2]} 个邮箱中，${match[1]} 个已验证或高可信。`,
  },
  {
    en: /^([\d.]+)% of companies have more than one stored contact\.$/,
    toZh: (match) => `${match[1]}% 的公司保存了多个联系人。`,
  },
];

function translateCore(value, language) {
  if (language === "en") return value;

  const dictionary = language === "zh-TW" ? EN_TO_ZH_TW : EN_TO_ZH_CN;
  const lowerDictionary =
    language === "zh-TW" ? EN_LOWER_TO_ZH_TW : EN_LOWER_TO_ZH_CN;
  const exact = dictionary.get(value) || lowerDictionary.get(value.toLowerCase());
  if (exact) return exact;

  for (const pattern of [...DYNAMIC_PATTERNS, ...EXTRA_DYNAMIC_PATTERNS]) {
    const match = value.match(pattern.en);
    if (!match) continue;

    const simplified = pattern.toZh(match);
    return language === "zh-TW" ? toTaiwanChinese(simplified) : simplified;
  }

  // Some MUI components compose a visible sentence from several known UI
  // phrases. Translate those phrases in-place without touching customer data.
  const safeEmbeddedEntries = [...dictionary.entries()]
    .filter(([english]) => english.includes(" ") && english.length >= 8)
    .sort((a, b) => b[0].length - a[0].length);

  let translated = value;
  safeEmbeddedEntries.forEach(([english, localized]) => {
    if (translated.includes(english)) {
      translated = translated.split(english).join(localized);
    }
  });

  return translated;
}

function translateValue(value, language) {
  if (!value || typeof value !== "string") return value;

  const sanitized = sanitizeTechnicalText(value);
  const leading = sanitized.match(/^\s*/)?.[0] || "";
  const trailing = sanitized.match(/\s*$/)?.[0] || "";
  const core = sanitized.trim();

  if (!core) return sanitized;

  return `${leading}${translateCore(core, language)}${trailing}`;
}

function translateNode(node, language) {
  if (!node || isTranslationExcluded(node)) return;

  if (node.nodeType === Node.TEXT_NODE) {
    if (!node.nodeValue?.trim()) return;

    const record = canonicalTextRecord(node);
    const translated = translateValue(record.source, language);
    record.rendered = translated;

    if (translated !== node.nodeValue) node.nodeValue = translated;
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return;

  ["placeholder", "title", "aria-label", "alt"].forEach((attribute) => {
    if (!node.hasAttribute?.(attribute)) return;

    const record = canonicalAttributeRecord(node, attribute);
    const translated = translateValue(record.source, language);
    record.rendered = translated;

    if (translated !== node.getAttribute(attribute)) {
      node.setAttribute(attribute, translated);
    }
  });
}

function translateTree(root, language) {
  if (!root) return;

  translateNode(root, language);

  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT
  );

  let current = walker.nextNode();

  while (current) {
    translateNode(current, language);
    current = walker.nextNode();
  }

  document.documentElement.lang = language;
  if (root === document.body && document.title) {
    document.title = translateValue(document.title, language);
  }
}

function defaultPosition() {
  return {
    x: window.innerWidth >= 900 ? 286 : 12,
    y: 92,
  };
}

export default function LanguageSwitcher() {
  const controlRef = useRef(null);
  const pointerRef = useRef(null);

  const [language, setLanguage] = useState(() => {
    const stored = window.localStorage.getItem(LANGUAGE_KEY);
    if (stored === "zh") return "zh-CN";
    return ["en", "zh-CN", "zh-TW"].includes(stored) ? stored : "en";
  });
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(POSITION_KEY));
      if (Number.isFinite(saved?.x) && Number.isFinite(saved?.y)) return saved;
    } catch (error) {
      // Ignore an invalid saved position.
    }

    return defaultPosition();
  });

  const clampPosition = (nextPosition) => {
    const rect = controlRef.current?.getBoundingClientRect();
    const width = rect?.width || (open ? 356 : 92);
    const height = rect?.height || 42;

    return {
      x: Math.min(
        Math.max(EDGE_GAP, nextPosition.x),
        Math.max(EDGE_GAP, window.innerWidth - width - EDGE_GAP)
      ),
      y: Math.min(
        Math.max(EDGE_GAP, nextPosition.y),
        Math.max(EDGE_GAP, window.innerHeight - height - EDGE_GAP)
      ),
    };
  };

  useEffect(() => {
    window.localStorage.setItem(LANGUAGE_KEY, language);
    translateTree(document.body, language);

    let framePending = false;

    const observer = new MutationObserver((mutations) => {
      if (framePending) return;
      framePending = true;

      window.requestAnimationFrame(() => {
        mutations.forEach((mutation) => {
          if (mutation.type === "characterData") {
            translateNode(mutation.target, language);
          }

          mutation.addedNodes.forEach((node) => translateTree(node, language));

          if (mutation.type === "attributes") {
            translateNode(mutation.target, language);
          }
        });

        framePending = false;
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["placeholder", "title", "aria-label", "alt"],
      characterData: true,
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [language]);

  useEffect(() => {
    const keepVisible = () => {
      setPosition((current) => {
        const next = clampPosition(current);
        window.localStorage.setItem(POSITION_KEY, JSON.stringify(next));
        return next;
      });
    };

    keepVisible();
    window.addEventListener("resize", keepVisible);

    return () => window.removeEventListener("resize", keepVisible);
  }, [open]);

  const chooseLanguage = (nextLanguage) => {
    setOpen(false);

    if (nextLanguage === language) return;

    window.localStorage.setItem(LANGUAGE_KEY, nextLanguage);
    setLanguage(nextLanguage);
  };

  const beginPointer = (event) => {
    if (open) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;

    pointerRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: position.x,
      startY: position.y,
      dragged: false,
    };

    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const movePointer = (event) => {
    const pointer = pointerRef.current;
    if (!pointer || pointer.pointerId !== event.pointerId) return;

    const dx = event.clientX - pointer.startClientX;
    const dy = event.clientY - pointer.startClientY;

    if (!pointer.dragged && Math.hypot(dx, dy) < 6) return;

    pointer.dragged = true;
    event.preventDefault();

    setPosition(
      clampPosition({
        x: pointer.startX + dx,
        y: pointer.startY + dy,
      })
    );
  };

  const endPointer = (event) => {
    const pointer = pointerRef.current;
    if (!pointer || pointer.pointerId !== event.pointerId) return;

    const dx = event.clientX - pointer.startClientX;
    const dy = event.clientY - pointer.startClientY;
    const dragged = pointer.dragged || Math.hypot(dx, dy) >= 6;

    if (dragged) {
      const next = clampPosition({
        x: pointer.startX + dx,
        y: pointer.startY + dy,
      });

      setPosition(next);
      window.localStorage.setItem(POSITION_KEY, JSON.stringify(next));
    } else {
      setOpen(true);
    }

    pointerRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const closedLabel =
    language === "zh-CN" ? "简中" : language === "zh-TW" ? "繁中" : "EN";

  const pillBase = {
    height: 38,
    minHeight: 38,
    borderRadius: "999px",
    px: 2.25,
    backgroundColor: "#EEF0F4",
    color: "#4E586E",
    boxShadow: "none",
    fontSize: "13px",
    fontWeight: 600,
    letterSpacing: "-0.01em",
    lineHeight: 1,
    textTransform: "none",
    transition: "all 180ms ease",
    "&:hover": {
      backgroundColor: "#E6E9EF",
      boxShadow: "none",
    },
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box
        ref={controlRef}
        sx={{
          position: "fixed",
          top: `${position.y}px`,
          left: `${position.x}px`,
          zIndex: 999999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 46,
          userSelect: "none",
        }}
      >
        {!open ? (
          <Fade in timeout={180}>
            <Button
              disableRipple
              onPointerDown={beginPointer}
              onPointerMove={movePointer}
              onPointerUp={endPointer}
              onPointerCancel={endPointer}
              aria-label="Open language selector"
              sx={{
                ...pillBase,
                minWidth: 92,
                cursor: "grab",
                touchAction: "none",
                "&:active": {
                  cursor: "grabbing",
                  transform: "scale(0.97)",
                },
              }}
            >
              {closedLabel}
            </Button>
          </Fade>
        ) : (
          <Fade in timeout={180}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                whiteSpace: "nowrap",
              }}
            >
              <Button
                disableRipple
                onClick={() => setOpen(false)}
                aria-label="Back"
                sx={{
                  ...pillBase,
                  width: 38,
                  minWidth: 38,
                  px: 0,
                  fontSize: "22px",
                  fontWeight: 400,
                }}
              >
                ‹
              </Button>

              <Button
                disableRipple
                onClick={() => chooseLanguage("en")}
                sx={{
                  ...pillBase,
                  minWidth: 82,
                  backgroundColor: language === "en" ? "#E3E8F3" : "#EEF0F4",
                  color: language === "en" ? "#182858" : "#4E586E",
                  "&:hover": {
                    backgroundColor: language === "en" ? "#DCE3F0" : "#E6E9EF",
                  },
                }}
              >
                English
              </Button>

              <Button
                disableRipple
                onClick={() => chooseLanguage("zh-CN")}
                sx={{
                  ...pillBase,
                  minWidth: 92,
                  backgroundColor: language === "zh-CN" ? "#E3E8F3" : "#EEF0F4",
                  color: language === "zh-CN" ? "#182858" : "#4E586E",
                  "&:hover": {
                    backgroundColor: language === "zh-CN" ? "#DCE3F0" : "#E6E9EF",
                  },
                }}
              >
                简体中文
              </Button>

              <Button
                disableRipple
                onClick={() => chooseLanguage("zh-TW")}
                sx={{
                  ...pillBase,
                  minWidth: 92,
                  backgroundColor: language === "zh-TW" ? "#E3E8F3" : "#EEF0F4",
                  color: language === "zh-TW" ? "#182858" : "#4E586E",
                  "&:hover": {
                    backgroundColor: language === "zh-TW" ? "#DCE3F0" : "#E6E9EF",
                  },
                }}
              >
                繁體中文
              </Button>
            </Box>
          </Fade>
        )}
      </Box>
    </ClickAwayListener>
  );
}

